import React, { useEffect, useState } from 'react';
import { DialogTitle, Typography, Button, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import {
  fetchUsersByOwner, setSelectedUser, clearUserData, clearUserMessages,
} from '../reduxStore/UserSlice';
import { User } from '../models/models';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonType } from '../constants/enums';
import MyTable from '../components/MyTable';
import { useDialog } from '../components/DialogComponent';
import UtilisateurForm from '../components/Forms/UtilisateurForm';
import BroadcastNotificationForm from '../components/Forms/BroadcastNotificationForm';
import CanDo from '../components/CanDo';
import { useSnackbar } from '../components/SnackbarProvider';
import { Chip } from '@mui/material';
import PeopleAltIcon  from '@mui/icons-material/PeopleAlt';
import CampaignIcon   from '@mui/icons-material/Campaign';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

const GestionUtilisateurs: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { userData, total, loading, error, successMessage, selectedUser } = useSelector(
    (state: any) => state.UserSlice
  );

  const [fetchParams, setFetchParams] = useState({ limit: 10, offset: 0 });

  const [FormDialog, openDialog, closeDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  const [BroadcastDialog, openBroadcast, closeBroadcast] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  useEffect(() => {
    dispatch(fetchUsersByOwner(fetchParams));
    return () => {
      dispatch(setSelectedUser(null));
      dispatch(clearUserData());
    };
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
    if (successMessage) showSnackbar(successMessage, 'success');
    if (error || successMessage) dispatch(clearUserMessages());
  }, [error, successMessage, dispatch, showSnackbar]);

  const handleCancel = () => {
    closeDialog();
    dispatch(setSelectedUser(null));
  };

  const handleEdit = (user: User) => {
    dispatch(setSelectedUser(user));
    openDialog();
  };

  const handlePageChange = (page: number) => {
    setFetchParams(prev => ({ ...prev, offset: (page - 1) * prev.limit }));
  };

  const headers: TableHeader<User>[] = [
    { key: 'login', label: 'Login' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Téléphone' },
    {
      key: 'roles_noms',
      label: 'Rôle(s)',
      render: (user) => user.roles_noms
        ? <Chip label={user.roles_noms} size="small" color="primary" variant="outlined" />
        : <Chip label="Aucun rôle" size="small" variant="outlined" />,
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (user) => (
        <Chip
          label={user.is_active ? 'Actif' : 'Inactif'}
          color={user.is_active ? 'success' : 'error'}
          size="small"
        />
      ),
    },
  ];

  const tableActions: TableAction<User>[] = [
    {
      label: 'Modifier',
      callback: handleEdit,
      type: ButtonType.EDIT,
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Gestion des utilisateurs"
        subtitle={`${total} utilisateur${total > 1 ? 's' : ''}`}
        icon={<PeopleAltIcon />}
        addLabel="Nouvel utilisateur"
        onAdd={openDialog}
      />

      <CanDo permission="notifications.send">
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CampaignIcon />}
            onClick={openBroadcast}
          >
            Diffuser une notification
          </Button>
        </Stack>
      </CanDo>

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={userData}
          actions={tableActions}
          onPageChange={handlePageChange}
          totalRows={total}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedUser ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
          </Typography>
        </DialogTitle>
        <UtilisateurForm onCancel={handleCancel} />
      </FormDialog>

      <BroadcastDialog>
        <DialogTitle>
          <Typography variant="h6">Diffuser une notification</Typography>
        </DialogTitle>
        <BroadcastNotificationForm onClose={closeBroadcast} />
      </BroadcastDialog>
    </AppPage>
  );
};

export default GestionUtilisateurs;
